import type { AxiosInstance } from 'axios';
import type { GetProvidersResponse, NewProviderPayload, GetTripQuotasResponse, GetProviderHistoryResponse, TripLimitUpdatePayload } from '../types/providers.types';
import { apiRoutes } from "@/shared/api/routes";
import { buildFullDayRange, normalizeDate } from '@/shared/utils/dateFilterUtils';
import { logger } from '@/shared/lib/logger';
import { getDateOnlyRangeForEnum, getServerTodayDateString } from '@/shared/utils/dateRangeFilterUtils';
import {
    getServerCalendarDate,
    formatServerStartOfDay,
    formatServerEndOfDay,
} from '@/shared/utils/serverTimeContext';

export const COMPANY_PREFERENCE_SELECT_FIELDS = ["id", "name"];
export const TRIP_QUOTA_SELECT_FIELDS = ["id", "span_type", "assigned_trip_count", "completed_trip_count", "total_trip_count", ["company", "id"]];

export const PROVIDER_SELECT_FIELDS = [
    "city", "code", "contact_email", "contact_name", "contact_phone1", "contact_phone2",
    "credentialing_api_key", "credentialing_id", "daily_trip_limit_max", "deleted",
    "edi_application_sender_code", "edi_attachment_transmission_code", "edi_billing_provider_address",
    "edi_billing_provider_city", "edi_billing_provider_postcode", "edi_billing_provider_state",
    "edi_billing_provider_tax_identifier", "edi_diagnosis_code", "edi_entity_identifier_code",
    "edi_interchange_sender_id", "edi_provider_organization_name", "edi_sender_status",
    "edi_submitter_comm_number", "edi_submitter_contact_name", "edi_submitter_name",
    "edi_submitter_primary_id", "eligibility_end", "eligibility_start",
    "exempted_from_location_codes", "external_provider_type", "id", "monthly_trip_limit_max",
    "name", "postcode", "provider_type", "street_address", "transportation_network_company", "transportation_subcontractor",
    "trip_limit_end_date", "trip_limit_start_date", "trips_only", "weekly_trip_limit_max",
    "yearly_trip_limit_max", "preference.id"
];

export const createProvidersApi = (apiClient: AxiosInstance) => ({
    getProviders: async (params: { page: number, rowsPerPage: number, sortConfig: any, query?: unknown[] }): Promise<GetProvidersResponse> => {
        const { page, rowsPerPage, sortConfig, query = [] } = params;
        const SORT_KEY_MAP: Record<string, string> = {
            priority: 'preference.id',
        };
        const order = sortConfig
            ? [[SORT_KEY_MAP[sortConfig.key] ?? sortConfig.key, sortConfig.direction === 'ascending' ? 'asc_nulls_last' : 'desc_nulls_last']]
            : [["mf_area_status", "asc_nulls_last"], ["name", "asc_nulls_last"]];

        const requestBody = {
            select: PROVIDER_SELECT_FIELDS,
            query: [...query],
            options: {
                timeout: 10,
                offset: page * rowsPerPage,
                limit: rowsPerPage,
                order,
                calculate_total: true,
                return_results: true,
                select_distinct: true
            }
        };

        try {
            const response = await apiClient.post<GetProvidersResponse>(
                apiRoutes.proxy('support/internal/company/select_query'),
                requestBody
            );
            return response.data;
        } catch (error) {
            logger.error('Failed to fetch providers:', error);
            throw new Error('Failed to load providers');
        }
    },

    getProviderDetailsById: async (id: string): Promise<GetProvidersResponse> => {
    const requestBody = {
      select: PROVIDER_SELECT_FIELDS,
      query: [["equal", "id", parseInt(id, 10)]],
      options: {
        limit: 1,
      },
    };

    try {
      const response = await apiClient.post<GetProvidersResponse>(
        apiRoutes.proxy('support/internal/company/select_query'),
        requestBody
      );
      return response.data;
    } catch (error) {
        logger.error(`Failed to fetch provider details for ID ${id}:`, error);
        throw new Error(`Failed to load provider details for ID ${id}`);
    }
},

getCompanyPreferences: async (ids?: number[]): Promise<GetProvidersResponse> => {
    const requestBody = {
        select: COMPANY_PREFERENCE_SELECT_FIELDS,
        query: ids && ids.length > 0 ? [['in', 'id', ids]] : [],
        options: {
            return_results: true,
            select_distinct: true,
        },
    };

    try {
        const response = await apiClient.post<GetProvidersResponse>(
            apiRoutes.proxy('support/internal/company_preference/select_query'),
            requestBody
        );
        return response.data;
    } catch (error) {
        logger.error('Failed to fetch company preferences:', error);
        throw new Error('Failed to load company preferences');
    }
},

getServiceAreas: async (offset: number, limit: number, filters?: any, searchTerm?: string): Promise<GetProvidersResponse> => {
    const query: any[] = [["null", "funding_source.id"]];

    if (searchTerm) {
        query.push(["like", "name", `%${searchTerm}%`]);
    }

    if (filters) {
        if (filters.fundingSourceIds && filters.fundingSourceIds.length > 0) {
            query.push(['in', 'funding_sources.id', filters.fundingSourceIds]);
        }
        if (filters.providerIds && filters.providerIds.length > 0) {
            query.push(['in', 'providers.id', filters.providerIds]);
        }
        if (filters.runIds && filters.runIds.length > 0) {
            query.push(['in', 'runs.id', filters.runIds]);
        }
    }

    const requestBody = {
        select: ["id", "name", "enclosure.id", "enclosure.name", "enclosure.xml_description"],
        query,
        options: {
            timeout: 10,
            offset,
            limit,
            order: [["enclosure.name", "asc_nulls_last"], ["id", "asc"]],
            calculate_total: true,
            return_results: true,
            select_distinct: true
        }
    };

    try {
        const response = await apiClient.post<GetProvidersResponse>(
            apiRoutes.proxy('support/internal/service_area/select_query'),
            requestBody
        );
        return response.data;
    } catch (error) {
        logger.error('Failed to fetch service areas:', error);
        throw new Error('Failed to load service areas');
    }
},

createProvider: async (providerData: NewProviderPayload) => {
    try {
        const response = await apiClient.post(
            apiRoutes.proxy('support/internal/company'),
            providerData
        );
        return response.data;
    } catch (error) {
        logger.error('Failed to create provider:', error);
        throw new Error('Failed to create provider');
    }
},

updateProvider: async (providerId: string, providerData: Partial<NewProviderPayload>) => {
    try {
        const response = await apiClient.put(
            apiRoutes.proxy(`support/internal/company/${providerId}`),
            { ...providerData, id: parseInt(providerId, 10) }
        );
        return response.data;
    } catch (error) {
        logger.error('Failed to update provider:', error);
        throw new Error('Failed to update provider');
    }
},

getCompanyTripCounts: async (companyId: string): Promise<GetTripQuotasResponse> => {
    const requestBody = {
        filter: ["id", "span_type", "assigned_trip_count", "completed_trip_count", "total_trip_count", ["company", "id"]],
        query: [["in", "company.id", [parseInt(companyId, 10)]]],
        options: {
            timeout: 10,
            offset: 0,
            limit: 1000,
            order: [["span_type", "asc_nulls_last"], ["id", "asc"]],
            calculate_total: false,
            return_results: true,
            select_distinct: true,
        },
    };

    try {
        const response = await apiClient.post<GetTripQuotasResponse>(
            apiRoutes.proxy('support/internal/company_trip_count/query'),
            requestBody
        );
        return response.data;
    } catch (error) {
        logger.error(`Failed to fetch trip counts for company ${companyId}:`, error);
        throw new Error(`Failed to load trip counts for company ${companyId}`);
    }
},

/**
 * Fetch live trip counts from the Trip table for a company.
 * Matches RoR CompanySingleTripCountSupport logic:
 * - assigned = status in [ASSIGNED, ACCEPTED, ACTIVE]
 * - completed = status in [COMPLETE, NOSHOW]
 * - total = status in [ASSIGNED, ACCEPTED, ACTIVE, COMPLETE, NOSHOW]
 *
 * Uses calculate_total with return_results:false to get counts efficiently.
 * NOTE: Trip table uses UPPERCASE statuses (not archive lowercase).
 */
getLiveTripCounts: async (
    companyId: number,
    startTime: string,
    endTime: string,
    spanType: string,
): Promise<{ assigned: number; completed: number; total: number }> => {
    const serverToday = getServerCalendarDate();
    const todayStart = formatServerStartOfDay(serverToday);
    const todayEnd = formatServerEndOfDay(serverToday);

    const baseQuery = spanType === 'DAY'
        ? [
            ['equal', 'metaroute.run.company.id', companyId],
            ['between', 'promised_pickup', startTime, endTime],
        ]
        : [
            ['equal', 'metaroute.run.company.id', companyId],
            ['between', 'promised_pickup', startTime, endTime],
            ['between', 'order_time', todayStart, todayEnd],
        ];

    const buildRequest = (extraConditions: unknown[][]) => apiClient.post(
        apiRoutes.proxy('support/internal/trip/select_query'),
        {
            select: [['count', 'id']],
            query: [...baseQuery, ...extraConditions],
            options: {
                timeout: 10,
                offset: 0,
                limit: 1,
                calculate_total: false,
                return_results: true,
                select_distinct: false,
            },
        },
    );

    const [assignedRes, completedRes, totalRes] = await Promise.all([
        buildRequest([['in', 'status', ['ASSIGNED', 'ACCEPTED', 'ACTIVE']]]),
        buildRequest([['in', 'status', ['COMPLETE', 'NOSHOW']]]),
        buildRequest([['in', 'status', ['ASSIGNED', 'ACCEPTED', 'ACTIVE', 'COMPLETE', 'NOSHOW']]]),
    ]);

    const extractCount = (res: { data?: { data?: unknown[] } }): number => {
        const rows = res?.data?.data;
        if (!Array.isArray(rows) || rows.length === 0) return 0;
        const first = rows[0];
        if (Array.isArray(first)) {
            return typeof first[0] === 'number' ? first[0] : 0;
        }
        return typeof first === 'number' ? first : 0;
    };

    return {
        assigned: extractCount(assignedRes),
        completed: extractCount(completedRes),
        total: extractCount(totalRes),
    };
},

/**
 * Fetch trip quota counts for multiple companies in a single API call.
 * Replaces the N individual getCompanyTripCounts calls with one batched request.
 */
getCompanyTripCountsBatch: async (companyIds: number[]): Promise<GetTripQuotasResponse> => {
    const requestBody = {
        filter: ["id", "span_type", "assigned_trip_count", "completed_trip_count", "total_trip_count", ["company", "id"]],
        query: [["in", "company.id", companyIds]],
        options: {
            timeout: 10,
            offset: 0,
            limit: 1000,
            order: [["span_type", "asc_nulls_last"], ["id", "asc"]],
            calculate_total: false,
            return_results: true,
            select_distinct: true,
        },
    };

    try {
        const response = await apiClient.post<GetTripQuotasResponse>(
            apiRoutes.proxy('support/internal/company_trip_count/query'),
            requestBody
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to load trip counts for providers');
    }
},
getProviderHistory: async (providerId: string, timePeriod?: string, customStartDate?: string, customEndDate?: string): Promise<GetProviderHistoryResponse> => {
    let startDateOnly: string | null = null;
    let endDateOnly: string | null = null;

    if (timePeriod === 'date' && customStartDate) {
        const selectedDate = normalizeDate(customStartDate) || getServerTodayDateString();
        startDateOnly = selectedDate;
        endDateOnly = selectedDate;
    } else if (timePeriod === 'date_range' && customStartDate && customEndDate) {
        startDateOnly = normalizeDate(customStartDate) || getServerTodayDateString();
        endDateOnly = normalizeDate(customEndDate) || startDateOnly;
    } else {
        switch (timePeriod) {
            case 'no_filter': {
                // No Filter = return ALL history with no date bound, matching
                // legacy behavior — previously this silently capped at the
                // last year, so counts and events didn't line up with
                // QA-React (ECOPD regression #88 on 2026-04-17).
                startDateOnly = null;
                endDateOnly = null;
                break;
            }
            case 'last_14_days':
            case 'previous_week':
            case 'current_week':
            case 'yesterday':
            case 'today':
            default: {
                const resolved = getDateOnlyRangeForEnum(timePeriod || 'last_14_days') ?? {
                    fromDate: getServerTodayDateString(),
                    toDate: getServerTodayDateString(),
                };
                startDateOnly = resolved.fromDate;
                endDateOnly = resolved.toDate ?? resolved.fromDate;
                break;
            }
        }
    }

    // Build the clause list conditionally — only include the date range when
    // the user actually requested one.
    const queryClauses: unknown[] = [
        ["and", ["equal", "key_id", providerId], ["equal", "event", "company"]],
        ["not", ["in", "action", ["login", "logout"]]],
    ];
    if (startDateOnly && endDateOnly) {
        const [startDate, endDate] = buildFullDayRange(startDateOnly, endDateOnly);
        queryClauses.unshift(["between", "timestamp", startDate, endDate]);
    }

    const requestBody = {
        query: queryClauses,
        options: {
            timeout: 10,
            offset: 0,
            limit: 50,
            order: [["timestamp", "desc"]],
            calculate_total: true,
            return_results: true,
            select_distinct: true
        }
    };

    try {
        const response = await apiClient.post<GetProviderHistoryResponse>(
            apiRoutes.proxy('support/internal/user_event/query'),
            requestBody
        );
        return response.data;
    } catch (error) {
        logger.error(`Failed to fetch history for provider ${providerId}:`, error);
        throw new Error(`Failed to load history for provider ${providerId}`);
    }
},

deleteProvider: async (providerId: string) => {
    try {
        const response = await apiClient.delete(
            apiRoutes.proxy(`support/internal/company/${providerId}`)
        );
        return response.data;
    } catch (error) {
        throw error;
    }
},

deleteAttachment: async (companyId: string, attachmentId: number) => {
    try {
        const response = await apiClient.delete(
            apiRoutes.proxy(`support/internal/company/${companyId}/file/${attachmentId}`)
        );
        return response.data;
    } catch (error) {
        logger.error(`Failed to delete attachment ${attachmentId} for company ${companyId}:`, error);
        throw error;
    }
},

uploadAttachment: async (companyId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
        // ECOPD #84: PDFs were silently failing because the backend
        // returns HTTP 200 with a `{ result: 'failure', errors: [...] }`
        // body for rejected mime types, and the mutation layer only
        // watched for thrown errors. Inspect the response body here and
        // surface a real Error so onError fires upstream.
        const response = await apiClient.post<{
            failure?: boolean;
            result?: string;
            errors?: Array<{ message?: string }>;
        }>(
            apiRoutes.upload(`support/internal/company/${companyId}/file`),
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        const data = response.data;
        if (data?.failure || data?.result === 'failure') {
            const msg = data?.errors?.[0]?.message || 'Failed to upload attachment';
            throw new Error(msg);
        }
        return data;
    } catch (error) {
        logger.error(`Failed to upload attachment for company ${companyId}:`, error);
        throw error;
    }
},

downloadAttachment: async (companyId: string, attachmentId: number): Promise<Blob> => {
    try {
        const response = await apiClient.get(
            apiRoutes.download(`support/internal/company/${companyId}/file/${attachmentId}`),
            { responseType: 'blob' }
        );
        return response.data as Blob;
    } catch (error) {
        logger.error(`Failed to download attachment ${attachmentId} for company ${companyId}:`, error);
        throw error;
    }
},

updateTripLimit: async (companyId: number, tripLimitData: TripLimitUpdatePayload) => {
    try {
        const response = await apiClient.put(
            apiRoutes.proxy(`support/internal/company/${companyId}/trip_limit`),
            tripLimitData
        );
        return response.data;
    } catch (error) {
        logger.error(`Failed to update trip limit for company ${companyId}:`, error);
        throw new Error(`Failed to update trip limit for company ${companyId}`);
    }
},

checkProviderExists: async (name: string): Promise<{ data: any[] }> => {
    const requestBody = {
        select: ["id"],
        query: [["equal", "name", name]],
        options: {
            timeout: 10,
            offset: 0,
            limit: 1,
            calculate_total: false,
            return_results: true,
            select_distinct: true,
        },
    };

    try {
        const response = await apiClient.post<{ data: any[] }>(
            apiRoutes.proxy('support/internal/company/select_query'),
            requestBody
        );
        return response.data;
    } catch (error) {
        logger.error(`Failed to check if provider "${name}" exists:`, error);
        throw new Error(`Failed to verify provider name`);
    }
},

/**
 * Check whether an external_provider_type is already taken by another provider (ECOPD-14326).
 * Optionally excludes a specific provider ID (used during update to exclude self).
 */
checkExternalProviderTypeUnique: async (
    externalProviderType: string,
    excludeProviderId?: number,
): Promise<{ data: Array<[number, string]> }> => {
    const query: unknown[] = [
        ['equal', 'external_provider_type', externalProviderType],
        ['equal', 'provider_type', 'EXTERNAL_PROVIDER'],
        ['null', 'deleted'],
    ];
    if (excludeProviderId !== undefined) {
        query.push(['not', ['equal', 'id', excludeProviderId]]);
    }

    const requestBody = {
        select: ['id', 'name'],
        query,
        options: {
            timeout: 10,
            offset: 0,
            limit: 1,
            calculate_total: false,
            return_results: true,
            select_distinct: true,
        },
    };

    try {
        const response = await apiClient.post<{ data: Array<[number, string]> }>(
            apiRoutes.proxy('support/internal/company/select_query'),
            requestBody
        );
        return response.data;
    } catch (error) {
        logger.error(`Failed to check uniqueness of external_provider_type "${externalProviderType}":`, error);
        throw new Error('Failed to verify external provider type uniqueness');
    }
},
});

export type ProvidersApi = ReturnType<typeof createProvidersApi>;
